import React, { useEffect, useMemo, useState } from 'react';
import hotelService from '../services/hotelService';
import './AddPaymentPorterWizard.css';

const HOTEL_LIST_LIMIT = 10;

// Shared hotel picker used by the PP / Opera Cloud Compare / Payment Migration Manager wizards and by
// the sync and vault create flows. Two modes:
//   • Controlled data:  pass `hotels` and the component just renders them (back-compat).
//   • Self-fetching:    pass `accountId` (chain) and/or `partnerId` and it loads the hotels itself,
//                       scoped so that with no chain ("All Hotel Chains") only the partner's hotels
//                       are returned (HP-75) — never other partners'.
// The wizard footer (Back/Next) renders only when `onNext` is provided, so it can be embedded inside a
// form/step that owns its own navigation (sync, vault).
//
// THERE IS NO CHAIN PICKER, deliberately. One was added here as an opt-in for flows reached with no
// chain in the top bar, and its only caller (the Universal API access wizard) has been changed to take
// the chain from the bar like every other screen — two chain selectors on screen saying different
// things is how a property gets provisioned under the wrong one. If a flow needs a chain, the reader
// picks it in the bar; do not re-add a second selector here.
const WizardHotelStep = ({
    hotels: hotelsProp,
    accountId,
    partnerId,
    selectedHotel,
    onSelect,
    error,
    title = 'Select Hotel',
    subtitle,
    onBack,
    onNext,
    loading,
    emptyMessage = 'No hotels are configured for this selection. Please add a hotel first.',
}) => {
    const [search, setSearch] = useState('');
    const [fetched, setFetched] = useState([]);
    const [fetching, setFetching] = useState(false);

    // Self-fetch only when the caller didn't supply a hotels array.
    const selfFetch = hotelsProp === undefined;
    useEffect(() => {
        if (!selfFetch) return;
        let cancelled = false;
        setFetching(true);
        const filters = {};
        if (accountId) filters.accountid = accountId;
        if (partnerId) filters.partnerid = partnerId;
        hotelService.getAllHotels(filters)
            .then(r => { if (!cancelled) setFetched(r.hotels || []); })
            .catch(() => { if (!cancelled) setFetched([]); })
            .finally(() => { if (!cancelled) setFetching(false); });
        return () => { cancelled = true; };
    }, [selfFetch, accountId, partnerId]);

    const hotels = selfFetch ? fetched : hotelsProp;

    const matches = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return hotels;
        return hotels.filter(h =>
            (h.hotelname || '').toLowerCase().includes(q) ||
            (h.hotelcode || '').toLowerCase().includes(q)
        );
    }, [hotels, search]);

    const shown = matches.slice(0, HOTEL_LIST_LIMIT);

    return (
    <div className="ppw-card">
        <div className="ppw-card-header">
            <h2 className="ppw-card-title">{title}</h2>
            {subtitle && <p className="ppw-card-subtitle">{subtitle}</p>}
        </div>
        <div className="ppw-card-body">
            {error && <span className="ppw-error-msg">{error}</span>}
            {fetching && <p className="ppw-card-subtitle">Loading hotels…</p>}
            {hotels.length > 0 && (
                <input
                    type="text"
                    className="ppw-input"
                    placeholder="Search by name or code..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ marginBottom: '0.75rem' }}
                />
            )}
            <div className="ppw-connector-list">
                {shown.length > 0 ? shown.map(h => (
                    <button
                        key={h.hotelid}
                        className={`ppw-connector-card${selectedHotel?.hotelid === h.hotelid ? ' selected' : ''}`}
                        onClick={() => onSelect(h)}
                    >
                        <div className="ppw-connector-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <div className="ppw-connector-info">
                            <div className="ppw-connector-name">{h.hotelname}</div>
                            {h.hotelcode && <div className="ppw-connector-meta">{h.hotelcode}</div>}
                        </div>
                    </button>
                )) : !fetching && (
                    <div className="ppw-empty-state">
                        <strong>No hotels found</strong>
                        {hotels.length === 0
                            ? emptyMessage
                            : 'No hotels match your search. Try a different name or code.'}
                    </div>
                )}
            </div>
            {matches.length > HOTEL_LIST_LIMIT && (
                <p className="ppw-card-subtitle" style={{ marginTop: '0.75rem' }}>
                    Showing {HOTEL_LIST_LIMIT} of {matches.length} — refine your search to narrow the list.
                </p>
            )}
        </div>
        {onNext && (
            <div className="ppw-footer">
                <button className="ppw-btn-back" onClick={onBack}>← Back</button>
                <button className="ppw-btn-next" onClick={onNext} disabled={!selectedHotel || loading}>
                    {loading ? 'Loading...' : 'Next →'}
                </button>
            </div>
        )}
    </div>
    );
};

export default WizardHotelStep;
